import { useEffect } from "react";
import { CaseStudyNav } from "./CaseStudyNav";
import { CaseStudyHero } from "./CaseStudyHero";
import { NarrativeShowcase } from "./NarrativeShowcase";
import { NarrativeGallery } from "./NarrativeGallery";
import { Moments } from "./Moments";
import { Pillars } from "./Pillars";
import { UseCaseForm } from "./UseCaseForm";
import { MoreUseCases } from "./MoreUseCases";
import { ScaleSection } from "../landing/ScaleSection";
import { LandingFooter } from "../landing/LandingFooter";
import type { UseCaseConfig } from "../../use-cases/types";

interface UseCasePageProps {
  config: UseCaseConfig;
}

/**
 * Single template for every use-case landing page. All variable content
 * comes from the `UseCaseConfig` passed in; adding a new vertical is a new
 * config file + a registry entry, with no changes here.
 */
export const UseCasePage = ({ config }: UseCasePageProps) => {
  useEffect(() => {
    const prevTitle = document.title;
    document.title = config.meta.title;
    return () => {
      document.title = prevTitle;
    };
  }, [config.meta.title]);

  // Deep links (e.g. /use-cases/education/#teaching-strategies from the home
  // page's case-study teaser): the browser tries the hash before React has
  // rendered the target, so scroll to it once it exists, and move focus
  // there too so keyboard and screen-reader users land on it. The target
  // needs tabIndex={-1} to take focus.
  useEffect(() => {
    let id: string;
    try {
      id = decodeURIComponent(window.location.hash.slice(1));
    } catch {
      return; // malformed fragment, e.g. "#%"
    }
    if (!id) return;
    const frame = requestAnimationFrame(() => {
      const target = document.getElementById(id);
      if (!target) return;
      target.scrollIntoView();
      target.focus({ preventScroll: true });
    });
    return () => cancelAnimationFrame(frame);
  }, []);

  return (
    <div className="bg-fwd-soft text-fwd-ink font-sans overflow-x-hidden">
      <CaseStudyNav navLabel={config.navLabel} ctaLabel={config.navCtaLabel} />
      <CaseStudyHero hero={config.hero} />
      {config.narrativeSample && (
        <NarrativeShowcase narrativeSample={config.narrativeSample} />
      )}
      {config.gallery && <NarrativeGallery gallery={config.gallery} />}
      {config.caseStudy}
      {config.moments && <Moments moments={config.moments} />}
      <ScaleSection
        eyebrow={config.scale.eyebrow}
        leadIn={config.scale.leadIn}
        firstHighlight={config.scale.firstHighlight}
        middle={config.scale.middle}
        secondHighlight={config.scale.secondHighlight}
        tail={config.scale.tail}
        subtitle={config.scale.subtitle}
        counters={config.scale.counters}
        footnote={config.scale.footnote}
      />
      <Pillars pillars={config.pillars} />
      {/* A page that carries a case study already shows it; the rest link to it. */}
      <MoreUseCases
        currentSlug={config.slug}
        includeCaseStudy={!config.caseStudy}
      />
      <UseCaseForm config={config} />
      <LandingFooter />
    </div>
  );
};
